import type { Distance } from "@pdfking/math"
import type { Math } from "@pdfking/quantity"
import { Ast, CM, MM } from "@pdfking/quantity"

import type { GrowEdge, GrowLayout } from "#public"
import {
  Along,
  Content,
  DIM_CONST,
  DIM_DEPTH,
  DIM_FLAP_SIZE,
  DIM_HEIGHT,
  DIM_WIDTH,
  Flap,
  FlapPiece,
  Fold,
  Pages,
  RecordByPrimaryKey,
  makeCoverEntry,
  GrowContentKind,
} from "#public"

// The back of the cover, pages 4 and 3, and the front, pages 1 and 2, each
// `size` along the opening; the book's spine as deep as the book.
const back = (size: Math): GrowLayout =>
  Content(GrowContentKind.PAGE, size, Pages(4, 3, { label: "B" }))
const front = (size: Math): GrowLayout =>
  Content(GrowContentKind.PAGE, size, Pages(1, 2, { label: "A" }))
const spine = (): GrowLayout => Content(GrowContentKind.SPINE, DIM_DEPTH)

// The boards turn round the block first, a flap in on its board after,
// standing a little off it.
const BOARD = Fold(90, 1)
const FLAP = Fold(180, 2, 0.25)
// A board's hinge turns with it, all but a groove's worth at the board
const GROOVE = Fold(5, 1)
const HINGE = Fold(85, 1)
// A board that never opens: the viewer stands it as far as it meets the
// other one
const STANDING = Fold(90, 0)

// Back, spine and front in a row, a flap `left` of the back or `right` of the
// front when the cover has one, as high as the page.
const wrap = (left: boolean, right: boolean): GrowLayout =>
  Along(
    [
      ...(left ? [FlapPiece(DIM_FLAP_SIZE), FLAP] : []),
      back(DIM_WIDTH),
      BOARD,
      spine(),
      BOARD,
      front(DIM_WIDTH),
      ...(right ? [FLAP, FlapPiece(DIM_FLAP_SIZE)] : []),
    ],
    DIM_HEIGHT
  )

// A board cover: the row of back, spine and front — a `hinge` between each
// board and the spine, creased on both edges, turning with the board but for a groove; the
// boards `inset` from the page —, the whole `taller` than the page, with a
// turn-in of `turn` off all four sides, each cut in at 45° at its ends so the
// corners fold in clean, glued down over the boards: never opening (step 0).
// The die is drawn off the tree, its own cut the outline.
const board = (turn: Distance, hinge: Distance, inset: Distance, taller: Distance): GrowLayout => {
  const panel = inset ? Ast.Sub(DIM_WIDTH, DIM_CONST(inset)) : DIM_WIDTH
  const left = hinge ? [GROOVE, FlapPiece(DIM_CONST(hinge)), HINGE] : [BOARD]
  const right = hinge ? [HINGE, FlapPiece(DIM_CONST(hinge)), GROOVE] : [BOARD]
  const turn_in = (edge: GrowEdge) =>
    Flap(edge, FlapPiece(DIM_CONST(turn)), {
      angle: 180,
      step: 0,
      taper: [DIM_CONST(turn), DIM_CONST(turn)],
    })
  return Along([
    Along(
      [back(panel), ...left, spine(), ...right, front(panel)],
      taller ? Ast.Add(DIM_HEIGHT, DIM_CONST(taller)) : DIM_HEIGHT
    ),
    turn_in("left"),
    turn_in("right"),
    turn_in("top"),
    turn_in("bottom"),
  ])
}

export const CoverGrowList = RecordByPrimaryKey([
  makeCoverEntry(
    "a",
    "NO_COVER",
    Along([back(DIM_WIDTH), front(DIM_WIDTH)], DIM_HEIGHT),
    { is_grow: false },
    { sides_tuple: [4], depth_molt: 2 }
  ),

  makeCoverEntry("c", "COVER", wrap(false, false), {}, { sides_tuple: [4], depth_molt: 2 }),

  makeCoverEntry(
    "cr",
    "RIGID_COVER",
    board(CM(2), MM(9), MM(4), MM(7)),
    { show_cut: false },
    { sides_tuple: [4], depth_molt: 2 }
  ),

  makeCoverEntry(
    "cs",
    "SEMI_RIGID_COVER",
    board(CM(2), MM(0), MM(0), MM(0)),
    { show_cut: false },
    { sides_tuple: [4], depth_molt: 2 }
  ),

  makeCoverEntry(
    "f1",
    "COVER_WITH_ONE_FLAP",
    wrap(false, true),
    {},
    { maximum_tickness: MM(0.36), depth_molt: 2, sides_tuple: [4] }
  ),

  makeCoverEntry(
    "f1r",
    "COVER_RIGHT_FLAP",
    wrap(true, false),
    {},
    { maximum_tickness: MM(0.36), depth_molt: 2, sides_tuple: [4] }
  ),

  makeCoverEntry(
    "f2",
    "COVER_WITH_TWO_FLAPS",
    wrap(true, true),
    {},
    { maximum_tickness: MM(0.36), depth_molt: 2, sides_tuple: [4] }
  ),

  // The calendar's two covers either side of its base, 24 mm wider than the
  // page each, the back upside down: they never open, standing off the base
  // till they meet over it, the pages hung where they do.
  makeCoverEntry(
    "cb",
    "CALENDAR_BASE",
    Along(
      [
        Content(
          GrowContentKind.PAGE,
          Ast.Add(DIM_WIDTH, DIM_CONST(MM(24))),
          Pages(2, 1, { label: "A" })
        ),
        STANDING,
        Content(GrowContentKind.SPINE, DIM_CONST(MM(80))),
        STANDING,
        Content(
          GrowContentKind.PAGE,
          Ast.Add(DIM_WIDTH, DIM_CONST(MM(24))),
          Pages(3, 4, { rotation: 180, label: "B" })
        ),
      ],
      DIM_HEIGHT
    ),
    {},
    { depth_molt: 2 }
  ),

  // The notepad's cover past its spine, a 2 cm turn-in glued under the pad,
  // opening with it as a back board would; its front a book cover's front,
  // page 1 out.
  makeCoverEntry(
    "cn",
    "NOTEPAD_COVER",
    Along([FlapPiece(DIM_CONST(CM(2))), BOARD, spine(), BOARD, front(DIM_WIDTH)], DIM_HEIGHT),
    {},
    { depth_molt: 1 }
  ),
] as const)
